import { Button } from "components/DesignSystem/Button/Button";
import Loader from "components/DesignSystem/Loader/loader";
import React, { Suspense, useRef, useState } from "react";
import Quill from "quill";
import Delta from "quill-delta";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import { usePinNoteMutation } from "store/apis/notes";
import { SmallWarningInfo } from "components/icons/SmallWarningInfo";
import classNames from "classnames";
import { extractMentionedProfileIds } from "util/extractMentionedProfileIds";
import type { MentionCandidate } from "components/QuillEditor/QuillEditor";

const QuillEditor = React.lazy(
  () => import("components/QuillEditor/QuillEditor")
);

export const EditNote = ({
  noteId,
  description,
  setEditableNoteId,
  resetNotes,
  mentionCandidates,
}: {
  noteId: string;
  description: string;
  setEditableNoteId: (id: string | null) => void;
  resetNotes: () => void;
  mentionCandidates?: MentionCandidate[];
}) => {
  const quillRef = useRef<Quill | null>(null);
  const [notesPayload, setNotesPayload] = useState<Delta | {}>(() => {
    try {
      const parsedDescription = JSON.parse(description);
      return typeof parsedDescription === "string"
        ? { ops: [{ insert: parsedDescription + "\n" }] }
        : parsedDescription;
    } catch {
      return { ops: [{ insert: description + "\n" }] };
    }
  });
  const [hasChanges, setHasChanges] = useState(false);
  const { alertToast } = useToast();
  const [pinNote, { isLoading }] = usePinNoteMutation();
  const [hasError, setHasError] = useState<boolean>(false);

  const handleTextChange = () => {
    const currentContent = quillRef?.current?.getContents() || {};
    setNotesPayload(currentContent);

    const initialContent = (() => {
      try {
        const parsedDescription = JSON.parse(description);
        return typeof parsedDescription === "string"
          ? { ops: [{ insert: parsedDescription + "\n" }] } // Convert plain text to Delta format
          : parsedDescription;
      } catch {
        return { ops: [{ insert: description + "\n" }] }; // Convert plain text to Delta format
      }
    })();

    setHasChanges(
      currentContent &&
        JSON.stringify(currentContent) !== JSON.stringify(initialContent)
    );
  };

  const handleSave = async () => {
    try {
      const taggedProfileIds = extractMentionedProfileIds(
        notesPayload as Parameters<typeof extractMentionedProfileIds>[0]
      );
      await pinNote({
        noteId,
        description: JSON.stringify(notesPayload),
        ...(taggedProfileIds.length > 0
          ? { tagged_profile_ids: taggedProfileIds }
          : {}),
      }).unwrap();
      resetNotes();
      setEditableNoteId(null);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <div className="t-p-4 t-rounded-lg t-border t-border-solid t-relative t-group t-border-neutral-0 t-bg-surface-lighter-grey">
      <div
        className={classNames(
          "t-bg-surface t-border t-border-solid t-rounded t-py-4 t-px-3 t-space-y-4",
          {
            "t-border-red": hasError,
            "t-border-surface-lighter-grey": !hasError,
          }
        )}
      >
        <Suspense fallback={<Loader size="small" />}>
          <QuillEditor
            className="t-max-h-80 t-overflow-y-auto"
            defaultValue={notesPayload}
            parentRef={quillRef}
            onTextChange={handleTextChange}
            onBlur={() => hasChanges && setHasError(true)}
            onFocus={() => setHasError(false)}
            mentionCandidates={mentionCandidates}
          />
        </Suspense>
        <div className="t-flex t-justify-end t-gap-2.5">
          <Button size="small" onClick={() => setEditableNoteId(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            customType="primary"
            onClick={handleSave}
            isLoading={isLoading}
            disabled={isLoading || !hasChanges}
          >
            Save edits
          </Button>
        </div>
      </div>
      {hasError && (
        <div className="t-flex t-gap-1 t-items-center t-mt-1">
          <SmallWarningInfo className="t-text-red" />
          <div className="t-text-body-sm t-text-red">
            You have unsaved changes. Save or cancel to continue.
          </div>
        </div>
      )}
    </div>
  );
};
